import { useTranslation } from 'react-i18next';
import { Heading } from 'react-aria-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUserDoctor } from '@fortawesome/free-solid-svg-icons';

// Types
import { OrderDetailsProp } from '../types';

// Helpers
import { formatDate, formatFullName, formatSex } from '../helpers';

// Styles
import styles from '../order-details.upp.module.scss';
import { DescriptionList, DescriptionPair } from './description-list';

type Props = {
  orderDetails: OrderDetailsProp;
};

export const PatientInformation = ({ orderDetails }: Props) => {
  const { t } = useTranslation();
  const patient = orderDetails?.patient;
  const patientFirstName = patient?.firstName;
  const patientLastName = patient?.lastName;
  const patientFullName =
    formatFullName(patientFirstName, patientLastName) ?? t('labels.na')!;
  const patientSex = formatSex(patient?.gender) ?? t('labels.na')!;
  const patientDob = formatDate(patient?.dob, t('labels.na')!);
  const patientBarcode = patient?.barcode || t('labels.na')!;
  const receivingProvider = orderDetails?.receivingProvider;
  const receivingProviderName = receivingProvider?.name || t('labels.na')!;
  const receivingProviderClinicName =
    receivingProvider?.clinicName || t('labels.na')!;

  const receivingProviderClinicLocation =
    receivingProvider?.clinicLocation || t('labels.na')!;

  const orderingProvider = orderDetails?.orderingProvider;
  const orderingProviderClinic =
    orderDetails?.orderingClinic || t('labels.na')!;
  const accessionDate = formatDate(
    orderDetails?.accessionDate || patient?.sampleReceived,
    t('labels.na'),
  );

  return (
    <section className={styles.sectionWrapper}>
      <div className={styles.patientInfoSectionWrapper}>
        <div className={styles.patientInfoWrapper}>
          <div className={styles.iconWrapper}>
            <FontAwesomeIcon
              icon={faUser}
              className={styles.icon}
              aria-hidden
            />
          </div>
          <div>
            <Heading level={2} className={styles.sectionHeading}>
              {t('orders.details.patientInformation')}
            </Heading>
            <DescriptionList>
              <DescriptionPair
                term={t('orders.details.name')}
                description={patientFullName}
              />
              <DescriptionPair
                term={t('orders.details.sex')}
                description={patientSex}
              />
              <DescriptionPair
                term={t('orders.details.dob')}
                description={patientDob}
              />
              <DescriptionPair
                term={t('orders.details.accessionNumber')}
                description={patientBarcode}
              />
            </DescriptionList>
          </div>
        </div>
        {receivingProvider ? (
          <div className={styles.patientInfoWrapper}>
            <div className={styles.iconWrapper}>
              <FontAwesomeIcon
                icon={faUserDoctor}
                className={styles.icon}
                aria-hidden
              />
            </div>
            <div>
              <div>
                <Heading level={2} className={styles.sectionHeading}>
                  {t('orders.details.receivingProvider')}
                </Heading>
                <DescriptionList>
                  <DescriptionPair
                    term={t('orders.details.provider.name')}
                    description={receivingProviderName}
                  />
                  <DescriptionPair
                    term={t('orders.details.provider.clinic')}
                    description={receivingProviderClinicName}
                  />
                  <DescriptionPair
                    term={t('orders.details.provider.address')}
                    description={receivingProviderClinicLocation}
                  />
                </DescriptionList>
              </div>
            </div>
          </div>
        ) : null}
        {orderingProvider ? (
          <div className={styles.patientInfoWrapper}>
            <div className={styles.iconWrapper}>
              <FontAwesomeIcon
                icon={faUserDoctor}
                className={styles.icon}
                aria-hidden
              />
            </div>
            <div>
              <div>
                <Heading level={2} className={styles.sectionHeading}>
                  {t('orders.details.orderingProvider')}
                </Heading>
                <DescriptionList>
                  <DescriptionPair
                    term={t('orders.details.provider.name')}
                    description={orderingProvider}
                  />
                  <DescriptionPair
                    term={t('orders.details.provider.clinic')}
                    description={orderingProviderClinic}
                  />
                </DescriptionList>
              </div>
            </div>
          </div>
        ) : null}
        <div className={styles.patientInfoWrapper}>
          <div className={styles.iconWrapper}>
            <FontAwesomeIcon
              icon={faUserDoctor}
              className={`${styles.icon} ${styles.iconTransparent}`}
              aria-hidden
            />
          </div>
          <div>
            <Heading level={2} className={styles.listHeading}>
              {t('orders.details.accessionDate')}:
            </Heading>
            <DescriptionList>
              <DescriptionPair
                term={t('orders.details.date')}
                description={accessionDate}
              />
            </DescriptionList>
          </div>
        </div>
      </div>
    </section>
  );
};
